import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Plus, Send } from 'lucide-react';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import DataTable from '../../components/ui/DataTable';
import Button from '../../components/ui/Button';
import GenericSelect from '../../components/ui/GenericSelect';
import TextInput from '../../components/ui/TextInput';
import CheckboxInput from '../../components/ui/CheckboxInput';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import api from '../../services/api';
import { getExpenseReportFilters } from '../../services/expenseReportService';
import { getLocations } from '../../services/locationService';
import { getExpenseCategories } from '../expense-reports/reportSections';

const PageHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
`;

const Dialog = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 33, 33, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 300;
`;

const DialogCard = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  width: 480px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DialogTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const DialogActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const FormatRow = styled.div`
  display: flex;
  gap: 16px;
`;

const SLUGS = ['department', 'category', 'employee', 'approval-process', 'behavior', 'vendor', 'comparison'];

const emptyForm = () => ({
  reportSlug: 'department',
  dayOfMonth: '3',
  xlsx: true,
  pdf: false,
  recipients: '',
  departmentId: null,
  locationId: null,
  categoryId: null,
});

export default function ExpenseReportSubscriptionsPage() {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageIndex: 1, pageSize: 20 });
  const [queryParams, setQueryParams] = useState({ pageNumber: 1, pageSize: 20 });
  const [refresh, setRefresh] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [runningId, setRunningId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null });
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    (async () => {
      setFetching(true);
      try {
        const res = await api.get('/v1/expense-report-subscriptions', { params: queryParams });
        const payload = res.data?.data;
        setData(payload?.data ?? []);
        setPagination({
          totalRowCount: payload?.totalRowCount ?? 0,
          pageIndex: payload?.pageIndex ?? 1,
          pageSize: payload?.pageSize ?? 20,
        });
      } catch { /* interceptor */ } finally {
        setFetching(false);
      }
    })();
  }, [queryParams, refresh]);

  useEffect(() => {
    (async () => {
      try {
        const res = await getExpenseReportFilters({});
        setDepartments(res.data?.data?.departments ?? []);
      } catch { /* interceptor */ }
      try {
        const res = await getLocations({ pageSize: 200, orderBy: 'name' });
        setLocations(res.data?.data?.data ?? []);
      } catch { /* interceptor */ }
      try {
        setCategories(await getExpenseCategories());
      } catch { /* interceptor */ }
    })();
  }, []);

  const handleRunNow = useCallback(async (id) => {
    setRunningId(id);
    try {
      await api.post(`/v1/expense-report-subscriptions/${id}/run-now`);
    } catch { /* interceptor */ } finally {
      setRunningId(null);
    }
  }, []);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'reportSlug',
      label: t('expenseReportSubs.report'),
      options: { filter: false, sort: false, customBodyRender: (v) => t(`expenseReports.tabs.${v}`, { defaultValue: v }) },
    },
    {
      name: 'dayOfMonth',
      label: t('expenseReportSubs.dayOfMonth'),
      options: { filter: false, sort: false, customBodyRender: (v) => t('expenseReportSubs.dayOfMonthValue', { day: v }) },
    },
    {
      name: 'formats',
      label: t('expenseReportSubs.formats'),
      options: { filter: false, sort: false, customBodyRender: (v) => (v || []).map((x) => x.toUpperCase()).join(', ') },
    },
    {
      name: 'recipientEmails',
      label: t('expenseReportSubs.recipients'),
      options: { filter: false, sort: false, customBodyRender: (v) => (v || []).join(', ') },
    },
    {
      name: 'lastSentPeriod',
      label: t('expenseReportSubs.lastSent'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => (v ? new Date(v).toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' }) : '—'),
      },
    },
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false,
        customBodyRender: (_, meta) => {
          const row = data[meta.rowIndex];
          if (!row) return null;
          return (
            <Button
              variant="outline"
              disabled={runningId === row.id}
              onClick={() => handleRunNow(row.id)}
              style={{ padding: '5px 10px', fontSize: 12 }}
            >
              <Send size={12} /> {t('expenseReportSubs.runNow')}
            </Button>
          );
        },
      },
    },
  ], [t, data, runningId, handleRunNow]);

  const recipientList = form.recipients.split(/[,;\n]/).map((s) => s.trim()).filter(Boolean);
  const isFormValid = form.reportSlug
    && Number(form.dayOfMonth) >= 1 && Number(form.dayOfMonth) <= 31
    && (form.xlsx || form.pdf)
    && recipientList.length > 0;

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await api.post('/v1/expense-report-subscriptions', {
        reportSlug: form.reportSlug,
        dayOfMonth: Number(form.dayOfMonth),
        formats: [form.xlsx ? 'xlsx' : null, form.pdf ? 'pdf' : null].filter(Boolean),
        recipientEmails: recipientList,
        departmentId: form.departmentId,
        locationId: form.locationId,
        categoryId: form.categoryId,
      });
      setDialogOpen(false);
      setForm(emptyForm());
      setRefresh((p) => p + 1);
    } catch { /* interceptor */ } finally {
      setSaving(false);
    }
  }, [form, recipientList]);

  const handleDelete = useCallback(async () => {
    try {
      await api.delete(`/v1/expense-report-subscriptions/${deleteConfirm.id}`);
      setRefresh((p) => p + 1);
    } catch { /* interceptor */ } finally {
      setDeleteConfirm({ isOpen: false, id: null });
    }
  }, [deleteConfirm.id]);

  return (
    <div>
      <Breadcrumbs items={[{ label: t('menu.expenseManagement') }, { label: t('menu.expenseReportSubs') }]} />
      <PageHeader>
        <Button onClick={() => { setForm(emptyForm()); setDialogOpen(true); }}>
          <Plus size={16} /> {t('expenseReportSubs.new')}
        </Button>
      </PageHeader>

      <DataTable
        title={t('expenseReportSubs.title')}
        data={data}
        columns={columns}
        fetching={fetching}
        pagination={pagination}
        onTableChange={(action, tableState) => {
          if (action === 'changePage' || action === 'changeRowsPerPage') {
            setQueryParams({ pageNumber: tableState.page + 1, pageSize: tableState.rowsPerPage });
          }
        }}
        options={{
          filter: false,
          search: false,
          onRowsDelete: (rowsDeleted) => {
            const id = data[rowsDeleted.data[0]?.dataIndex]?.id;
            if (id) setDeleteConfirm({ isOpen: true, id });
            return false;
          },
        }}
      />

      {dialogOpen && (
        <Dialog onClick={(e) => { if (e.target === e.currentTarget) setDialogOpen(false); }}>
          <DialogCard>
            <DialogTitle>{t('expenseReportSubs.new')}</DialogTitle>
            <GenericSelect
              label={t('expenseReportSubs.report')}
              options={SLUGS.map((s) => ({ id: s, name: t(`expenseReports.tabs.${s}`) }))}
              value={form.reportSlug}
              onChange={(v) => setForm((p) => ({ ...p, reportSlug: v || 'department' }))}
              placeholder={t('expenseReportSubs.report')}
            />
            <TextInput
              label={t('expenseReportSubs.dayOfMonth')}
              type="number"
              min="1"
              max="31"
              value={form.dayOfMonth}
              onChange={(e) => setForm((p) => ({ ...p, dayOfMonth: e.target.value }))}
              required
            />
            <FormatRow>
              <CheckboxInput
                label="XLSX"
                checked={form.xlsx}
                onChange={(e) => setForm((p) => ({ ...p, xlsx: e.target.checked }))}
              />
              <CheckboxInput
                label="PDF"
                checked={form.pdf}
                onChange={(e) => setForm((p) => ({ ...p, pdf: e.target.checked }))}
              />
            </FormatRow>
            <TextInput
              label={t('expenseReportSubs.recipients')}
              value={form.recipients}
              onChange={(e) => setForm((p) => ({ ...p, recipients: e.target.value }))}
              placeholder={t('expenseReportSubs.recipientsHint')}
              required
            />
            <GenericSelect
              label={t('expenseReports.filters.department')}
              options={departments}
              value={form.departmentId}
              onChange={(v) => setForm((p) => ({ ...p, departmentId: v || null }))}
              placeholder={t('expenseReportSubs.optionalScope')}
            />
            <GenericSelect
              label={t('expenseReports.filters.location')}
              options={locations}
              value={form.locationId}
              onChange={(v) => setForm((p) => ({ ...p, locationId: v || null }))}
              placeholder={t('expenseReportSubs.optionalScope')}
            />
            <GenericSelect
              label={t('expenseReports.filters.category')}
              options={categories}
              value={form.categoryId}
              onChange={(v) => setForm((p) => ({ ...p, categoryId: v || null }))}
              placeholder={t('expenseReportSubs.optionalScope')}
            />
            <DialogActions>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button onClick={handleSave} disabled={!isFormValid || saving}>{t('common.save')}</Button>
            </DialogActions>
          </DialogCard>
        </Dialog>
      )}

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('expenseReportSubs.deleteTitle')}
        message={t('expenseReportSubs.deleteMessage')}
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, id: null })}
      />
    </div>
  );
}
